import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface InputPromptModalProps {
	open: boolean;
	title: string;
	description?: string;
	placeholder?: string;
	confirmLabel?: string;
	cancelLabel?: string;
	defaultValue?: string;
	loading?: boolean;
	onClose: () => void;
	onConfirm: (value: string) => void;
}

/**
 * Modal to prompt the user for a value
 * @param props.open - Whether the modal is open
 * @param props.title - The title of the modal
 * @param props.description - The description of the modal
 * @param props.placeholder - The placeholder for the input
 * @param props.confirmLabel - The label for the confirm button
 * @param props.cancelLabel - The label for the cancel button
 * @param props.defaultValue - The default value for the input
 * @param props.loading - Whether the modal is loading
 * @param props.onClose - The function to call when the modal is closed
 * @param props.onConfirm - The function to call when the confirm button is clicked
 */
export default function InputPromptModal(props: InputPromptModalProps) {
	const [value, setValue] = useState(props.defaultValue ?? "");

	useEffect(() => {
		if (props.open) setValue(props.defaultValue ?? "");
	}, [props.open, props.defaultValue]);

	const isConfirmDisabled = useMemo(
		() => props.loading || value.trim().length === 0,
		[props.loading, value],
	);

	return (
		<Dialog
			open={props.open}
			onOpenChange={(o) => (!o ? props.onClose() : undefined)}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{props.title}</DialogTitle>
					{props.description ? (
						<DialogDescription>{props.description}</DialogDescription>
					) : null}
				</DialogHeader>
				<div className="py-2">
					<Input
						value={value}
						onChange={(e) => setValue(e.target.value)}
						placeholder={props.placeholder ?? "Titel"}
						autoFocus
						onKeyDown={(e) => {
							if (e.key === "Enter" && !isConfirmDisabled) {
								e.preventDefault();
								props.onConfirm(value.trim());
							}
						}}
					/>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={props.onClose}
						disabled={props.loading}
					>
						{props.cancelLabel ?? "Abbrechen"}
					</Button>
					<Button
						onClick={() => props.onConfirm(value.trim())}
						disabled={isConfirmDisabled}
					>
						{props.confirmLabel ?? "Erstellen"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
